import { Component, OnInit, Inject, NgZone } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ProfileService } from 'src/app/pages/account/services/profile.service';
import { Router } from '@angular/router';
import { LsHelperService } from '../../services/ls-helper.service';

@Component({
  selector: 'prgov-profile-selection-modal',
  templateUrl: './profile-selection-modal.component.html',
  styleUrls: ['./profile-selection-modal.component.scss']
})
export class ProfileSelectionModalComponent implements OnInit {

  profileList: any[] = [];
  filteredProfileList: any[] = [];
  selectedProfile: any = null;
  currentProfile: any = null;
  selectedProfileData: any = null; // Datos completos del perfil seleccionado
  loading: boolean = false;
  errorMessage: string = '';
  page = 1;
  size = 10; // Cargar 10 perfiles iniciales
  term: any = null;
  searchTerm: string = '';
  private searchTimeout: any;
  showInitialMessage: boolean = false; // No mostrar mensaje inicial
  minSearchLength: number = 3; // Mínimo de caracteres para buscar

  constructor(
    public dialogRef: MatDialogRef<ProfileSelectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private profileService: ProfileService,
    private ngZone: NgZone,
    private router: Router,
    private ls: LsHelperService
  ) {
    // NO inicializar aquí, hacerlo en ngOnInit para tener prioridad correcta
  }

  ngOnInit() {
    // Cargar el perfil seleccionado con la siguiente prioridad:
    // 1. localStorage (fuente de verdad)
    // 2. data.currentProfile (backup si no hay en localStorage)
    this.loadSelectedProfileFromStorage();

    // Si no se cargó desde localStorage, intentar con data
    if (!this.selectedProfile && this.data && this.data.currentProfile) {
      this.currentProfile = this.data.currentProfile;
      this.selectedProfile = this.data.currentProfile.Id || this.data.currentProfile.id;
      this.selectedProfileData = this.data.currentProfile;
    }

    // Cargar automáticamente los primeros 10 perfiles
    this.loadProfiles();
  }

  // Cargar el perfil seleccionado desde localStorage (fuente de verdad)
  loadSelectedProfileFromStorage() {
    try {
      const profileData = localStorage.getItem('selectedProfile');
      if (profileData && profileData !== 'null') {
        const profile = JSON.parse(profileData);
        this.selectedProfile = profile.Id || profile.id;
        this.selectedProfileData = profile;
        this.currentProfile = profile; // También actualizar currentProfile
      }
    } catch (error) {
      console.error('Error al cargar el perfil seleccionado desde localStorage:', error);
    }
  }

  // Carga la lista de perfiles médicos disponibles
  loadProfiles(forceRefresh: boolean = false) {
    this.loading = true;
    this.errorMessage = '';

    // Usar el término de búsqueda si existe
    const searchTerm = this.searchTerm && this.searchTerm.trim() !== ''
      ? this.searchTerm
      : (this.term == '' ? 'null' : this.term);

    this.profileService
      .search({
        term: searchTerm,
        pageNumber: this.page,
        pageSize: this.size,
      }, forceRefresh)
      .then((data: any) => {
        this.ngZone.run(() => {
          this.loading = false;
          this.profileList = data;
          this.filteredProfileList = data;
        });
      })
      .catch(error => {
        this.ngZone.run(() => {
          this.loading = false;
          this.errorMessage = 'No se pudieron cargar los perfiles. Por favor, intente nuevamente.';
        });
      });
  }

  // Maneja el evento de input del campo de búsqueda
  onSearchInput(event: any) {
    this.searchTerm = event.target.value;
    this.showInitialMessage = false;

    // Cancelar el timeout anterior si existe
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Si el término es muy corto, cargar los primeros 10 perfiles
    if (this.searchTerm.trim().length < this.minSearchLength) {
      this.size = 10; // Volver a cargar solo 10 perfiles
      this.loadProfiles();
      return;
    }

    // Aumentar el tamaño de búsqueda cuando hay término de búsqueda
    this.size = 50;

    // Esperar 300ms después de que el usuario deje de escribir (más rápido que antes)
    this.searchTimeout = setTimeout(() => {
      this.onSearchChange();
    }, 300);
  }

  // Busca perfiles en el servidor según el término de búsqueda
  onSearchChange() {
    // Solo buscar si hay suficientes caracteres
    if (this.searchTerm.trim().length >= this.minSearchLength) {
      this.loadProfiles(true);
    }
  }

  // Obtiene el objeto completo del perfil seleccionado
  getSelectedProfileObject() {
    if (!this.selectedProfile) return null;

    // Si ya tenemos los datos cargados desde localStorage, usarlos
    if (this.selectedProfileData && (this.selectedProfileData.Id || this.selectedProfileData.id) === this.selectedProfile) {
      return this.selectedProfileData;
    }

    // Sino, buscar en la lista de perfiles cargados
    return this.profileList.find(profile =>
      (profile.Id || profile.id) === this.selectedProfile
    );
  }

  // Selecciona un perfil de la lista
  onSelectProfile(profile: any) {
    this.selectedProfile = profile.Id || profile.id;
    this.selectedProfileData = profile; // Guardar los datos completos
  }

  /**
   * Verifica si un perfil está seleccionado
   * @param profile El perfil a verificar
   * @returns true si el perfil está seleccionado
   */
  isProfileSelected(profile: any): boolean {
    return (profile.Id || profile.id) === this.selectedProfile;
  }

  /**
   * Verifica si un perfil es el actual
   * @param profile El perfil a verificar
   * @returns true si el perfil es el actual
   */
  isCurrentProfile(profile: any): boolean {
    if (!this.currentProfile) return false;
    return (profile.Id || profile.id) === (this.currentProfile.Id || this.currentProfile.id);
  }

  /**
   * Selecciona un perfil y cierra el modal
   */
  selectProfile() {
    if (this.selectedProfile) {
      // Obtener el perfil completo (puede estar en localStorage o en la lista de búsqueda)
      const selectedProfileObj = this.getSelectedProfileObject();

      if (selectedProfileObj) {
        // Retornar el perfil completo para que auth.service lo maneje
        // El auth.service se encarga de guardarlo en localStorage y disparar el evento
        this.dialogRef.close(selectedProfileObj);
      } else {
        // Si no se encontró el perfil, buscar por ID
        console.warn('No se encontró el objeto del perfil seleccionado');
        this.dialogRef.close({ Id: this.selectedProfile });
      }
    }
  }

  /**
   * Cancela la selección y cierra el modal
   */
  cancel() {
    this.dialogRef.close();
  }

  /**
   * Cierra el diálogo y redirige a la página para crear un nuevo perfil
   */
  addNewProfile() {
    this.dialogRef.close();
    this.router.navigate(['/account/profiles/new']);
  }

  maskSSN(ssn: string): string {
    if (!ssn) {
      return '';
    }
    const cleanSSN = ssn.replace(/[-\s]/g, '');
    if (cleanSSN.length < 4) {
      return '***-**-****';
    }
    const lastFour = cleanSSN.slice(-4);
    return `***-**-${lastFour}`;
  }
}
